import {
  PAGINATION,
  ROUTE_STATUSES,
  STOP_SKIP_REASONS,
  formatDate,
  type Route,
  type RouteStop,
} from '@fdt/shared';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RouteIcon from '@mui/icons-material/Timeline';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api, errorKey } from '../api/client';
import { FormDialog } from '../components/FormDialog';
import { RouteMap } from '../components/RouteMap';
import { RoutePlayback, deviationRangesOf, type PlaybackState } from '../components/RoutePlayback';
import { trackingApi } from '../resources/tracking';
import { useFormError } from '../hooks/useFormError';
import { currentLocale } from '../i18n';
import { customersApi, warehousesApi } from '../resources';
import { routesApi } from '../resources/routes';

const STATUS_COLOUR: Record<Route['status'], 'default' | 'info' | 'warning' | 'success'> = {
  draft: 'default',
  assigned: 'info',
  started: 'warning',
  completed: 'success',
};

/** One stop row, draggable while the route can still be edited. */
function SortableStop({
  stop,
  editable,
  selected,
  onSelect,
  onRemove,
}: {
  stop: RouteStop;
  editable: boolean;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.id,
    disabled: !editable,
  });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      onClick={onSelect}
      sx={{
        p: 1,
        mb: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        borderColor: selected ? 'primary.main' : undefined,
        borderWidth: selected ? 2 : 1,
        cursor: 'pointer',
      }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {editable && (
          <Box
            {...attributes}
            {...listeners}
            sx={{ display: 'flex', cursor: 'grab', color: 'text.disabled' }}
            aria-label={t('routes.dragToReorder')}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}

        <Chip
          size="small"
          label={stop.sequence}
          color={
            stop.status === 'visited' ? 'success' : stop.status === 'skipped' ? 'error' : 'primary'
          }
          sx={{ minWidth: 34 }}
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap title={stop.customerName}>
            {stop.customerName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[stop.customerCode, stop.customerAddress, stop.customerComune]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {stop.deliveryWindowStart && stop.deliveryWindowEnd && (
            <Typography variant="caption" color="text.secondary" display="block">
              {stop.deliveryWindowStart.slice(0, 5)}–{stop.deliveryWindowEnd.slice(0, 5)}
            </Typography>
          )}
        </Box>

        {stop.distanceFromPreviousMeters !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            +{(stop.distanceFromPreviousMeters / 1000).toFixed(1)} km
          </Typography>
        )}

        {stop.status === 'skipped' && (
          <Tooltip title={stop.skipNote ?? t(`routes.skipReasons.${stop.skipReason ?? 'other'}`)}>
            <Chip size="small" color="error" variant="outlined" label={t('routes.skipped')} />
          </Tooltip>
        )}

        {editable && (
          <IconButton
            size="small"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            aria-label={t('common.delete')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
}

export function RouteEditorPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);

  /**
   * The recorded track is loaded on request, never with the page.
   *
   * Reading it is a logged access to an employee's movements (DECISIONS D97),
   * and a screen that fetches it on open would log an access every time anyone
   * glanced at a route — which makes the log useless for the question it exists
   * to answer.
   */
  const [showTrack, setShowTrack] = useState(false);
  const [playback, setPlayback] = useState<PlaybackState>({ index: null, cursor: null });

  const track = useQuery({
    queryKey: ['tracking', 'track', id],
    queryFn: () => trackingApi.routeTrack(id),
    enabled: Boolean(id) && showTrack,
  });
  const [message, setMessage] = useState<string | null>(null);
  const actionError = useFormError();

  const route = useQuery({
    queryKey: ['routes', id],
    queryFn: () => routesApi.get(id),
    enabled: Boolean(id),
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<{ osmTileUrl: string; defaultWarehouseId: string | null }>('/settings')).data,
    staleTime: 5 * 60 * 1000,
  });

  const warehouses = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => warehousesApi.list({ page: 1, pageSize: PAGINATION.maxPageSize }),
  });

  // Only customers with a pin can be routed to, so the picker asks for those.
  const [customerSearch, setCustomerSearch] = useState('');
  const pickable = useQuery({
    queryKey: ['customers', 'pinned', customerSearch],
    queryFn: () =>
      customersApi.list({
        page: 1,
        pageSize: 50,
        hasLocation: 'true',
        isActive: 'true',
        search: customerSearch || undefined,
        sortBy: 'businessName',
      }),
  });

  const editable = route.data?.status === 'draft' || route.data?.status === 'assigned';

  const depot = useMemo(() => {
    const warehouseId =
      route.data?.startWarehouseId ?? settings.data?.defaultWarehouseId ?? null;
    const warehouse = (warehouses.data?.items ?? []).find((item) => item.id === warehouseId);
    return warehouse?.location ?? null;
  }, [route.data, settings.data, warehouses.data]);

  function applyRoute(updated: Awaited<ReturnType<typeof routesApi.get>>): void {
    queryClient.setQueryData(['routes', id], updated);
    void queryClient.invalidateQueries({ queryKey: ['routes'], exact: false });
  }

  function run<TArgs extends unknown[]>(
    fn: (...args: TArgs) => Promise<Awaited<ReturnType<typeof routesApi.get>>>,
    successKey?: string,
  ) {
    return async (...args: TArgs) => {
      actionError.clear();
      try {
        applyRoute(await fn(...args));
        if (successKey) {
          setMessage(t(successKey));
        }
      } catch (error) {
        actionError.capture(error);
      }
    };
  }

  const addStops = useMutation({
    mutationFn: (customerIds: string[]) => routesApi.addStops(id, { customerIds }),
    onSuccess: applyRoute,
    onError: (error) => actionError.capture(error),
  });

  const reorder = useMutation({
    mutationFn: (stopIds: string[]) => routesApi.reorder(id, stopIds),
    onSuccess: applyRoute,
    onError: (error) => actionError.capture(error),
  });

  const optimize = useMutation({
    mutationFn: () => routesApi.optimize(id),
    onSuccess: async (result) => {
      applyRoute(await routesApi.get(id));
      const saved =
        result.previousDistanceMeters === null
          ? null
          : result.previousDistanceMeters - result.distanceMeters;
      setMessage(
        saved === null
          ? t('routes.optimised', { km: (result.distanceMeters / 1000).toFixed(1) })
          : t('routes.optimisedWithSaving', {
              km: (result.distanceMeters / 1000).toFixed(1),
              saved: (saved / 1000).toFixed(1),
            }),
      );
    },
    onError: (error) => actionError.capture(error),
  });

  const computeGeometry = useMutation({
    mutationFn: () => routesApi.computeGeometry(id),
    onSuccess: (updated) => {
      applyRoute(updated);
      setMessage(t('routes.geometryComputed'));
    },
    onError: (error) => actionError.capture(error),
  });

  // --- duplicate dialog ---
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState('');
  const duplicateError = useFormError();
  const duplicate = useMutation({
    mutationFn: () => routesApi.duplicate(id, { routeDate: duplicateDate }),
    onSuccess: (created) => {
      setDuplicateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['routes'] });
      navigate(`/routes/${created.id}`);
    },
    onError: (error) => duplicateError.capture(error),
  });

  // --- skip dialog ---
  const [skipStopId, setSkipStopId] = useState<string | null>(null);
  const [skipReason, setSkipReason] = useState<(typeof STOP_SKIP_REASONS)[number]>('customer_closed');
  const [skipNote, setSkipNote] = useState('');
  const skipError = useFormError();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id || !route.data) {
      return;
    }
    const ids = route.data.stops.map((stop) => stop.id);
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    reorder.mutate(next);
  }

  if (route.isPending) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (route.isError || !route.data) {
    return <Alert severity="error">{t(errorKey(route.error))}</Alert>;
  }

  const detail = route.data;
  const busy =
    addStops.isPending ||
    reorder.isPending ||
    optimize.isPending ||
    computeGeometry.isPending;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton onClick={() => navigate('/routes')} aria-label={t('common.back')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h1">
          {formatDate(`${detail.routeDate}T00:00:00Z`, locale)}
          {detail.name ? ` — ${detail.name}` : ''}
        </Typography>
        <Chip color={STATUS_COLOUR[detail.status]} label={t(`routes.statuses.${detail.status}`)} />
        {busy && <CircularProgress size={18} />}
      </Stack>

      {actionError.errorKey && (
        <Alert severity="error" onClose={actionError.clear}>
          {t(actionError.errorKey)}
        </Alert>
      )}

      {detail.geometryStale && detail.stopCount > 0 && (
        <Alert
          severity="info"
          action={
            <Button size="small" onClick={() => computeGeometry.mutate()} disabled={busy}>
              {t('routes.computeGeometry')}
            </Button>
          }
        >
          {t('routes.staleNotice')}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        {/* --- left: stops --- */}
        <Card sx={{ width: { xs: '100%', lg: 420 }, flexShrink: 0 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h2">{t('routes.stops')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {detail.plannedDistanceMeters !== null
                  ? `${(detail.plannedDistanceMeters / 1000).toFixed(1)} km · ${Math.round(
                      (detail.plannedDurationSeconds ?? 0) / 60,
                    )} ${t('routes.minutes')}`
                  : t('routes.notPlanned')}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ my: 2 }} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                startIcon={<AutoFixHighIcon />}
                onClick={() => optimize.mutate()}
                disabled={!editable || busy || detail.stopCount < 2}
              >
                {t('routes.optimize')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RouteIcon />}
                onClick={() => computeGeometry.mutate()}
                disabled={busy || detail.stopCount === 0}
              >
                {t('routes.computeGeometry')}
              </Button>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => {
                  setDuplicateDate(detail.routeDate);
                  duplicateError.clear();
                  setDuplicateOpen(true);
                }}
              >
                {t('routes.duplicate')}
              </Button>
            </Stack>

            {editable && (
              <Autocomplete
                options={pickable.data?.items ?? []}
                getOptionLabel={(option) => `${option.code} — ${option.businessName}`}
                // Already-added customers would be refused by the API anyway.
                getOptionDisabled={(option) =>
                  detail.stops.some((stop) => stop.customerId === option.id)
                }
                filterOptions={(options) => options}
                onInputChange={(_, value) => setCustomerSearch(value)}
                onChange={(_, value) => {
                  if (value) {
                    addStops.mutate([value.id]);
                    setCustomerSearch('');
                  }
                }}
                value={null}
                loading={pickable.isFetching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('routes.addCustomer')}
                    helperText={t('routes.addCustomerHelp')}
                    size="small"
                  />
                )}
                sx={{ mb: 2 }}
              />
            )}

            {detail.stops.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                {t('routes.noStops')}
              </Box>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={detail.stops.map((stop) => stop.id)}
                strategy={verticalListSortingStrategy}
              >
                {detail.stops.map((stop) => (
                  <SortableStop
                    key={stop.id}
                    stop={stop}
                    editable={Boolean(editable)}
                    selected={stop.id === selectedStopId}
                    onSelect={() => setSelectedStopId(stop.id)}
                    onRemove={() => void run(() => routesApi.removeStop(id, stop.id))()}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>

        {/* --- right: map and route settings --- */}
        <Stack spacing={2} sx={{ flexGrow: 1, width: '100%' }}>
          <Card>
            <CardContent>
              <RouteMap
                stops={detail.stops.map((stop) => ({
                  id: stop.id,
                  label: stop.customerName,
                  location: stop.location,
                  sequence: stop.sequence,
                  status: stop.status,
                }))}
                geometry={detail.plannedGeometry}
                depot={depot}
                tileUrl={settings.data?.osmTileUrl ?? 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'}
                selectedStopId={selectedStopId}
                onStopClick={setSelectedStopId}
                track={track.data?.pings.map((ping) => ping.location) ?? null}
                cursor={playback.cursor}
                deviationRanges={track.data ? deviationRangesOf(track.data) : undefined}
              />
            </CardContent>
          </Card>

          {/* Only offered once the route has been driven: before that there is
              nothing recorded, and a button that always answers "no positions"
              teaches people to ignore it. Reading a track is a logged access to
              someone's location data, so it is never loaded automatically. */}
          {detail.status !== 'draft' && detail.status !== 'assigned' && (
            <Card>
              <CardContent>
                <Typography variant="h2" gutterBottom>
                  {t('tracking.playback')}
                </Typography>

                {!showTrack ? (
                  <Button variant="outlined" onClick={() => setShowTrack(true)}>
                    {t('tracking.loadTrack')}
                  </Button>
                ) : track.isPending ? (
                  <CircularProgress size={24} />
                ) : track.isError ? (
                  <Alert severity="error">{t(errorKey(track.error))}</Alert>
                ) : track.data ? (
                  <RoutePlayback track={track.data} onCursorChange={setPlayback} />
                ) : null}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h2" gutterBottom>
                {t('routes.settings')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <TextField
                  select
                  label={t('routes.startWarehouse')}
                  value={detail.startWarehouseId ?? ''}
                  onChange={(event) =>
                    void run(() =>
                      routesApi.update(id, { startWarehouseId: event.target.value || null }),
                    )()
                  }
                  helperText={t('routes.startWarehouseHelp')}
                  disabled={!editable}
                  fullWidth
                >
                  <MenuItem value="">{t('routes.defaultWarehouse')}</MenuItem>
                  {(warehouses.data?.items ?? []).map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label={t('fields.status')}
                  value={detail.status}
                  onChange={(event) =>
                    void run(() =>
                      routesApi.update(id, { status: event.target.value as Route['status'] }),
                    )()
                  }
                  helperText={t('routes.statusHelp')}
                  fullWidth
                >
                  {ROUTE_STATUSES.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                      // The server enforces the path; the menu just reflects it.
                      disabled={status !== detail.status && !isReachable(detail.status, status)}
                    >
                      {t(`routes.statuses.${status}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {detail.assignedUserName && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('routes.assignee')}: {detail.assignedUserName}
                    {detail.vehiclePlate ? ` · ${detail.vehiclePlate}` : ''}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Stack>

      <FormDialog
        open={duplicateOpen}
        title={t('routes.duplicateTitle')}
        onClose={() => setDuplicateOpen(false)}
        onSubmit={() => duplicate.mutate()}
        submitting={duplicate.isPending}
        errorKey={duplicateError.errorKey}
        maxWidth="xs"
      >
        <TextField
          label={t('routes.duplicateDate')}
          type="date"
          value={duplicateDate}
          onChange={(event) => setDuplicateDate(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText={t('routes.duplicateHelp')}
          required
          fullWidth
          sx={{ mt: 1 }}
        />
      </FormDialog>

      <FormDialog
        open={Boolean(skipStopId)}
        title={t('routes.skipTitle')}
        onClose={() => setSkipStopId(null)}
        onSubmit={() => {
          if (skipStopId) {
            void run(() =>
              routesApi.updateStop(id, skipStopId, {
                status: 'skipped',
                skipReason,
                skipNote: skipNote || null,
              }),
            )();
            setSkipStopId(null);
          }
        }}
        errorKey={skipError.errorKey}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('fields.reason')}
            value={skipReason}
            onChange={(event) =>
              setSkipReason(event.target.value as (typeof STOP_SKIP_REASONS)[number])
            }
            fullWidth
          >
            {STOP_SKIP_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {t(`routes.skipReasons.${reason}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('fields.notes')}
            value={skipNote}
            onChange={(event) => setSkipNote(event.target.value)}
            required={skipReason === 'other'}
            helperText={skipReason === 'other' ? t('routes.skipNoteRequired') : undefined}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormDialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={5000}
        onClose={() => setMessage(null)}
        message={message ?? ''}
      />
    </Stack>
  );
}

/** Mirrors the server's allowed status moves, for disabling menu entries. */
function isReachable(from: Route['status'], to: Route['status']): boolean {
  const allowed: Record<Route['status'], Route['status'][]> = {
    draft: ['assigned'],
    assigned: ['draft', 'started'],
    started: ['completed'],
    completed: [],
  };
  return allowed[from].includes(to);
}
